import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
} from "@devhop/ui";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { LayersIcon, MousePointerClickIcon, PlusIcon } from "lucide-react";
import { useMemo } from "react";
import type { FormFieldDTO } from "@/modules/forms/presentation/api/types";
import { AddFieldMenu } from "@/modules/forms/presentation/ui/builder/AddFieldMenu";
import { FormSortableFieldRow } from "@/modules/forms/presentation/ui/builder/FormSortableFieldRow";
import type { FormFieldType } from "@/server/platform/db/schema/forms";

type FormBuilderProps = {
  fields: FormFieldDTO[];
  formStatus: string;
  disabledReorder?: boolean;
  canEditFields: boolean;
  onReorder: (orderedIds: string[]) => void;
  onAddField: (type: FormFieldType) => void;
  onEditField: (field: FormFieldDTO) => void;
  onRemoveField: (fieldId: string) => void;
  isReorderPending?: boolean;
  canAddResponse?: boolean;
  onAddResponse?: () => void;
};

export function FormBuilder({
  fields,
  formStatus,
  disabledReorder,
  canEditFields,
  onReorder,
  onAddField,
  onEditField,
  onRemoveField,
  isReorderPending,
  canAddResponse,
  onAddResponse,
}: FormBuilderProps) {
  const ordered = useMemo(
    () => [...fields].sort((a, b) => a.order - b.order),
    [fields],
  );

  const ids = useMemo(() => ordered.map((f) => f.id), [ordered]);

  const questionCount = useMemo(
    () => ordered.filter((f) => f.type !== "section").length,
    [ordered],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (evt: DragEndEvent) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const nextOrder = arrayMove(ordered, oldIndex, newIndex);
    onReorder(nextOrder.map((f) => f.id));
  };

  const isDraft = formStatus === "draft";
  const canRemoveFields = isDraft && canEditFields;

  const dragDisabled =
    !!disabledReorder ||
    !!isReorderPending ||
    !canEditFields ||
    formStatus === "closed";

  return (
    <Card>
      <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-lg">ຄຳຖາມໃນຟອມ</CardTitle>
            <Badge variant="outline">{questionCount} ຄຳຖາມ</Badge>
            <Badge variant="outline">{ordered.length} ລາຍການ</Badge>
          </div>
          <CardDescription className="flex items-start gap-2">
            <MousePointerClickIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              ກົດບັດເພື່ອແກ້ໄຂ · ລາກທີ່ຂອບຂວາເພື່ອຈັດລຳດັບ
            </span>
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canAddResponse && onAddResponse ? (
            <Button type="button" variant="outline" onClick={onAddResponse}>
              <PlusIcon className="mr-2 h-4 w-4" />
              ເພີ່ມຄຳຕອບ
            </Button>
          ) : null}
          <AddFieldMenu disabled={!canEditFields} onAdd={onAddField} />
        </div>
      </CardHeader>
      <Separator />
      <CardContent className="space-y-4">
        {ordered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed bg-muted/20 px-6 py-12 text-center">
            <LayersIcon className="h-10 w-10 text-muted-foreground/60" />
            <div className="space-y-1">
              <p className="font-medium">ຍັງບໍ່ມີຄຳຖາມ</p>
              <p className="text-muted-foreground text-sm">
                ເລີ່ມດ້ວຍການເພີ່ມຟິວແບບຂໍ້ຄວາມ ຫຼື ພາກກຳນົດ
              </p>
            </div>
            <AddFieldMenu disabled={!canEditFields} onAdd={onAddField} />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {ordered.map((f, idx) => (
                  <FormSortableFieldRow
                    key={f.id}
                    field={f}
                    index={idx}
                    disabled={dragDisabled}
                    canRemove={canRemoveFields}
                    onEdit={onEditField}
                    onRemove={onRemoveField}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
        {formStatus === "closed" ? (
          <p className="text-muted-foreground text-xs">
            ຟອມປິດແລ້ວ — ບໍ່ສາມາດແກ້ໄຂຄຳຖາມອີກຕໍ່ໄປ
          </p>
        ) : null}
        {isReorderPending ? (
          <p className="text-muted-foreground text-xs">ກຳລັງບັນທຶກລຳດັບ…</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
